// The audio lab's recordings (issue #3708), the parts that can be wrong without an AudioContext: what
// a recording's sidecar says, where an audio-clock time falls in it, and what a recorded double-talk
// run uploads. The engine (audio_lab_engine.ts) owns the context and calls these; the tests run them
// under Vitest.
//
// A lab recording has two channels, the microphone and the guide reference (everything the engine
// played, from its guide bus), aligned sample for sample, and starts at `startTimeSec` on the audio
// clock, the clock every time in the session log is on. A recorded double-talk run uploads its WAV
// with a sidecar that holds everything the offline canceller tests need to cut and score the take
// without the session log. The sidecar travels in the recording route's query string
// (server/src/audio_lab_routes.ts), so it carries numbers only: the per-frame traces stay in the
// session log's `measure.doubleTalk` entry.

import { RecordingChannel, recordingChannels } from '../../../../audio/guide_reference.ts';
import { DetectorConfig } from '../../../../audio/pitch/types.ts';
import type { DoubleTalkPhase, DoubleTalkResult, GuideOptions, MicOptions } from './audio_lab_engine.ts';

export interface LabRecording {
  samples: Float32Array; // the microphone
  reference: Float32Array; // the guide reference
  sampleRate: number;
  startTimeSec: number | undefined; // audio clock at samples[0]; undefined when nothing was recorded
}

// What every lab recording's sidecar says about its audio, beside whatever the caller adds
export interface RecordingAudioInfo {
  sampleRate: number;
  channels: RecordingChannel[];
  startTimeSec?: number;
  samples: number;
  seconds: number;
}

export function recordingMetadata<Extra extends object>(
  recording: LabRecording,
  extra: Extra,
): Extra & RecordingAudioInfo {
  const { sampleRate, startTimeSec, samples } = recording;
  return {
    ...extra,
    sampleRate,
    channels: recordingChannels, // the order the WAV holds them in
    startTimeSec,
    samples: samples.length,
    seconds: samples.length / sampleRate,
  };
}

// Half-open sample range [start, end) of a recording
export interface SampleRange {
  start: number;
  end: number;
}

// Where a span of the audio clock falls in a recording, as sample offsets into it, clipped to the
// samples it holds: a span that starts before the recording's first sample starts at 0, one that
// ends after its last ends at its length, and one wholly outside it is empty (start equals end)
export function sampleRangeOf(fromSec: number, toSec: number, recording: LabRecording): SampleRange {
  const { startTimeSec, sampleRate, samples } = recording;
  if (startTimeSec === undefined) {
    return { start: 0, end: 0 };
  }
  const clip = (seconds: number) =>
    Math.min(samples.length, Math.max(0, Math.round((seconds - startTimeSec) * sampleRate)));
  const start = clip(fromSec);
  return { start, end: Math.max(start, clip(toSec)) };
}

// The recorded run's label: the page's record label when set, the guide kind and the gain in
// percent (the server keeps letters, digits, '-' and '_' and turns the rest into '_')
export function doubleTalkRecordingLabel(recordLabel: string, guide: Pick<GuideOptions, 'kind' | 'gain'>): string {
  const run = `doubletalk-${guide.kind}-gain${Math.round(guide.gain * 100)}`;
  const label = recordLabel.trim();
  return label ? `${label}-${run}` : run;
}

// Whether a recorded double-talk run uploads its recording. `survived` is whether the recording was
// still running, on the mic the run started on, when the run ended: stop() ends a recording and
// discards it, and what is left afterwards is not the run's take. An aborted run uploads nothing and
// needs no error of its own (its result says why); a recording that did not survive, or holds no
// samples, is discarded with the reason in `error`, for the result's recordingError.
export function recordedRunVerdict(run: { aborted?: string; survived: boolean; samples: number }): {
  upload: boolean;
  error?: string;
} {
  if (!run.survived) {
    return { upload: false, error: 'the mic stopped during the run; the recording was discarded' };
  }
  if (run.aborted) {
    return { upload: false };
  }
  if (run.samples === 0) {
    return { upload: false, error: 'nothing was recorded' };
  }
  return { upload: true };
}

// One phase as the sidecar holds it: the logged phase without its trace, and its span and the
// window its rates were scored over as sample offsets into the recording
export type DoubleTalkRecordedPhase = Omit<DoubleTalkPhase, 'trace'> & {
  startSample?: number;
  endSample?: number;
  scoredFromSample?: number;
  scoredToSample?: number;
};

export interface DoubleTalkRecordingContext {
  label: string;
  guide: Pick<GuideOptions, 'kind' | 'gain'>;
  mic: MicOptions | undefined;
  context: unknown; // the engine's getContextInfo(): sample rate, base and output latency, state
  track: MediaTrackSettings | undefined; // what the browser actually applied, beside what `mic` asked for
  detector: DetectorConfig;
  userAgent: string;
}

// What a recorded run adds to the sidecar; the upload adds the audio's own fields (recordingMetadata)
export function doubleTalkRecordingMetadata(
  recording: LabRecording,
  result: DoubleTalkResult,
  run: DoubleTalkRecordingContext,
) {
  const phases: DoubleTalkRecordedPhase[] = result.phases.map(({ trace: _trace, ...phase }) => {
    const recorded: DoubleTalkRecordedPhase = { ...phase };
    if (phase.startSec !== undefined && phase.endSec !== undefined) {
      const span = sampleRangeOf(phase.startSec, phase.endSec, recording);
      recorded.startSample = span.start;
      recorded.endSample = span.end;
    }
    if (phase.scoredFromSec !== undefined && phase.scoredToSec !== undefined) {
      const scored = sampleRangeOf(phase.scoredFromSec, phase.scoredToSec, recording);
      recorded.scoredFromSample = scored.start;
      recorded.scoredToSample = scored.end;
    }
    return recorded;
  });
  const { singerHz, singerMidi, otherMidi } = result;
  const { label, guide, mic, context, track, detector, userAgent } = run;
  return {
    kind: 'audio-lab-double-talk' as const,
    label,
    guide,
    mic,
    track,
    detector,
    context,
    userAgent,
    result: { singerHz, singerMidi, otherMidi, phases },
  };
}
